/** Body composes the existing joint and region tools (ADR-037).
 * Capabilities decide which tools mount; hidden tools are disposed.
 * Depends on injected panel factories and capability reads, never the renderer.
 */
import type { PanelMount, PanelHandle } from './panel-registry.js';
import { progressCount, type BodyProgress } from './body-progress.js';
import { statusMark } from './status-mark.js';

/** `intake` (ADR-040) leads when a World check is open; hidden otherwise. */
export const BODY_SECTIONS = ['intake', 'joints', 'parts', 'hair', 'soft'] as const;
export type BodySection = (typeof BODY_SECTIONS)[number];

export function createBodyPanel(deps: {
  readonly hands: PanelMount;
  readonly joints: PanelMount;
  readonly parts: PanelMount;
  /** ADR-038: the tail's spring card, under Tails & cloth (optional: absent = never offered). */
  readonly spring?: PanelMount;
  readonly showSpring?: () => boolean;
  /** ADR-038 J2: the soft-tissue cards (breast / belly / glute leaves), their own section. */
  readonly softTissue?: PanelMount;
  readonly showSoftTissue?: () => boolean;
  /** ADR-040: the World check, the first section while an intake session is open (optional: absent = never offered). */
  readonly intake?: PanelMount;
  readonly showIntake?: () => boolean;
  /** Re-render when the World check opens, closes or changes (it lives outside the store). */
  readonly intakeChanged?: (listener: () => void) => () => void;
  /**
   * One section ONLY (INTAKE_GUIDED_FLOW §3.4 C1b: adding fingers shows Body › Joints and nothing
   * else): while it names a section, the others are hidden, it is open, and no Next row is drawn.
   */
  readonly focus?: () => BodySection | null;
  readonly focusChanged?: (listener: () => void) => () => void;
  /** ADR-039: the Hair card, its own section (optional: absent = never offered). */
  readonly hair?: PanelMount;
  readonly showHair?: () => boolean;
  /** Re-render when the SUBJECT is remounted (a remount notifies nothing in the store). */
  readonly subjectChanged?: (listener: () => void) => () => void;
  readonly showHands: () => boolean;
  readonly showJoints: () => boolean;
  readonly showParts: () => boolean;
  /**
   * The header marks and denominators (XD note 2026-09-27 §3.1): one mark per
   * object and a count, drawn in each section's summary after its title.
   * Optional: absent = title only, as before.
   */
  readonly progress?: () => BodyProgress;
  /** Redraw the headers when a reading changes outside the store (a hand being pinned, say). */
  readonly progressChanged?: (listener: () => void) => () => void;
  /** The last section's Next crosses to Look (XD note §3.2; Allen ruled 2026-09-27 Q3: the only tab crossing). */
  readonly openLook?: () => void;
}): PanelMount {
  /*
   * The reveal's memory outlives a mount. Leaving pose mode or switching tabs REMOUNTS this panel,
   * and a per-mount memory re-ran the first-view reveal every time — invisible while a hand still
   * `needs` (it reopened Joints), until a touched section began to read complete (2026-09-28) and
   * the rerun threw the person from Joints to Soft tissue. Once per character means once.
   */
  const memory: { who: string | null; register: string | null; open: Set<BodySection>; intakeFor: string | null } = { who: null, register: null, open: new Set(), intakeFor: null };
  return (container, store) => {
    const root = document.createElement('div');
    root.className = 'body-panel';
    container.append(root);
    const mounted = new Map<string, { node: HTMLElement; handle: PanelHandle }>();
    const sections = new Map<BodySection, HTMLDetailsElement>();
    const headers = new Map<BodySection, HTMLElement>();
    const TITLES: Readonly<Record<BodySection, string>> = { intake: 'World check', joints: 'Joints', parts: 'Tails & cloth', hair: 'Hair', soft: 'Soft tissue' };
    /*
     * THE SLOW REVEAL (XD note 2026-09-27 §3.2; Allen ruled: fold on the Next PRESS, the last Next
     * crosses to Look, Timeline stays outside). In CREATOR the sections are an accordion: one open
     * at a time; on first view (and each new character) the first section holding a `needs` or
     * `suggested` object opens and the rest fold — when everything is settled they all start
     * folded, the headers reading as the receipt. Next closes its section and opens the following
     * one; it is never disabled — primary when nothing in the section needs you, quiet (with one
     * neutral line) when something does, which is the skip. A header click opens any section (the
     * expert's jump); `openSurface` opens one and the toggle folds the rest. In EXPERT every
     * section is independent and open, and Next is hidden.
     */
    const creator = (): boolean => store.getLayout().register !== 'expert';
    /**
     * `memory.who` is the character the reveal last ran for. A RERUN of the same character (growing a
     * leaf, a tail, an edit) rebuilds the subject too, and must not re-fold the section the person is
     * working in (the jiggle e2e caught the chest card folding away the moment its leaf grew). A new
     * mount for the same character restores the sections that were open, once.
     */
    let restored = false;
    const characterKey = (): string => {
      const t = store.getDocument().target;
      return `${t.specimen.uri}|${t.label}`;
    };
    const nextRows = new Map<BodySection, HTMLElement>();
    /**
     * A tail on offer leads (Allen, 2026-10-03, the ape's missed tail): Tails & cloth comes before Joints, so
     * the first card the person sees is that recommendation — order, reveal and Next all follow it.
     */
    const tailFirst = (): boolean =>
      deps.progress !== undefined && deps.progress().parts.objects.some((o) => (o.subject === 'Tail?' || o.subject === 'Stub tail?') && o.state === 'suggested');
    const order = (): readonly BodySection[] => (tailFirst() ? ['intake', 'parts', 'joints', 'hair', 'soft'] : BODY_SECTIONS);
    const visible = (): BodySection[] => order().filter((id) => !sections.get(id)!.hidden);
    const openOnly = (id: BodySection | null): void => {
      for (const [sid, node] of sections) node.open = sid === id;
    };
    /** A complete section (touched, or all settled) has nothing pending: what was left alone was a choice. */
    const pending = (id: BodySection): number => {
      if (deps.progress === undefined) return 0;
      const p = deps.progress()[id];
      return p.complete ? 0 : p.objects.filter((o) => o.state === 'needs').length;
    };
    const firstUnsettled = (): BodySection | null => {
      if (deps.progress === undefined) return visible()[0] ?? null;
      const progress = deps.progress();
      return visible().find((id) => !progress[id].complete && progress[id].objects.some((o) => o.state === 'needs' || o.state === 'suggested')) ?? null;
    };
    for (const [id, title] of BODY_SECTIONS.map((sid) => [sid, TITLES[sid]] as const)) {
      const section = document.createElement('details');
      section.className = 'body-section';
      section.dataset['section'] = id;
      const summary = document.createElement('summary');
      const name = document.createElement('span');
      name.className = 'body-section-title';
      name.textContent = title;
      summary.append(name);
      if (deps.progress !== undefined) {
        const marks = document.createElement('span');
        marks.className = 'body-progress';
        summary.append(marks);
        headers.set(id, marks);
      }
      section.append(summary);
      section.addEventListener('toggle', () => {
        if (section.open) memory.open.add(id);
        else memory.open.delete(id);
        if (section.open && creator()) for (const [sid, node] of sections) if (sid !== id && node.open) node.open = false;
      });
      const next = document.createElement('div');
      next.className = 'body-next';
      nextRows.set(id, next);
      root.append(section);
      sections.set(id, section);
    }
    /** Each visible section's Next row, kept last in the section (tools mount by append). */
    const renderNext = (): void => {
      const order = visible();
      for (const id of BODY_SECTIONS) {
        const row = nextRows.get(id)!;
        const section = sections.get(id)!;
        section.append(row);
        row.hidden = !creator() || section.hidden;
        if (row.hidden) continue;
        const at = order.indexOf(id);
        const following = order[at + 1];
        const open = pending(id);
        const children: HTMLElement[] = [];
        if (open > 0) {
          const line = document.createElement('p');
          line.className = 'body-next-left';
          // Shortened (XD 2026-09-29 §3B): the count is the decision; the Next button beside it IS "you can come back".
          line.textContent = `${open} still ${open === 1 ? 'needs' : 'need'} you.`;
          children.push(line);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = open > 0 ? 'hand-revert body-next-button' : 'hand-graft-now body-next-button';
        button.dataset['next'] = following ?? 'look';
        button.textContent = following !== undefined ? `Next: ${TITLES[following]} ›` : 'Next: Look ›';
        button.addEventListener('click', () => {
          if (following === undefined) {
            section.open = false;
            deps.openLook?.();
            return;
          }
          const target = sections.get(following)!;
          target.open = true; // the toggle folds the rest
          section.open = false;
          target.scrollIntoView({ block: 'nearest' });
        });
        children.push(button);
        row.replaceChildren(...children);
      }
    };
    /** First view per character and register: Creator opens the first unsettled section; Expert opens all. */
    const reveal = (): void => {
      const register = creator() ? 'creator' : 'expert';
      const who = characterKey();
      if (who === memory.who && register === memory.register) {
        if (!restored) for (const [sid, node] of sections) node.open = memory.open.has(sid);
        restored = true;
        return;
      }
      memory.register = register;
      memory.who = who;
      restored = true;
      if (register === 'expert') for (const node of sections.values()) node.open = true;
      else openOnly(firstUnsettled());
    };
    /** One mark per object, then the count ("2 of 3", "1 suggested"). */
    const renderHeaders = () => {
      if (deps.progress === undefined) return;
      const progress = deps.progress();
      for (const id of BODY_SECTIONS) {
        const host = headers.get(id)!;
        const p = progress[id];
        const marks = p.objects.map(o => {
          const mark = statusMark(o.state, { pill: null, subject: o.subject, ...(o.hue === undefined ? {} : { hue: o.hue }) });
          if (o.faint === true) mark.dataset['faint'] = 'true';
          return mark;
        });
        const count = document.createElement('span');
        count.className = 'body-progress-count';
        count.textContent = progressCount(p);
        host.replaceChildren(...marks, count);
        host.hidden = p.total === 0;
      }
    };
    const sync = (key: string, parent: HTMLElement, offered: boolean, mount: PanelMount) => {
      const existing = mounted.get(key);
      if (!offered && existing) { existing.handle.dispose(); existing.node.remove(); mounted.delete(key); }
      if (offered && !existing) {
        const node = document.createElement('div');
        node.dataset['tool'] = key;
        parent.append(node);
        mounted.set(key, { node, handle: mount(node, store) });
      }
    };
    const render = () => {
      // The DOM follows the order (a tail offer moves Tails & cloth above Joints, and back when it is answered).
      for (const id of order()) root.append(sections.get(id)!);
      const intake = sections.get('intake')!;
      const intakeOffered = deps.intake !== undefined && (deps.showIntake?.() ?? false);
      if (deps.intake !== undefined) sync('intake', intake, intakeOffered, deps.intake);
      intake.hidden = !intakeOffered;
      // A World check that OPENS — or whose file has just reached the stage (a new character, which the
      // reveal below would otherwise fold away) — is the person's next step: it opens, and in Creator the
      // rest fold. Remembered per character across mounts (the reveal's lesson above): a tab switch must
      // not reopen it.
      const opened = intakeOffered && memory.intakeFor !== characterKey();
      memory.intakeFor = intakeOffered ? characterKey() : null;
      const joints = sections.get('joints')!;
      const parts = sections.get('parts')!;
      const handsOffered = deps.showHands();
      const jointsOffered = deps.showJoints();
      const partsOffered = deps.showParts();
      sync('hands', joints, handsOffered, deps.hands);
      sync('joints', joints, jointsOffered, deps.joints);
      // Reorder when a newly offered hand mounts after an existing elbow.
      const hands = mounted.get('hands');
      const jointTools = mounted.get('joints');
      if (hands && jointTools) joints.insertBefore(hands.node, jointTools.node);
      sync('parts', parts, partsOffered, deps.parts);
      const springOffered = deps.spring !== undefined && (deps.showSpring?.() ?? false);
      if (deps.spring !== undefined) sync('spring', parts, springOffered, deps.spring);
      const hair = sections.get('hair')!;
      const hairOffered = deps.hair !== undefined && (deps.showHair?.() ?? false);
      if (deps.hair !== undefined) sync('hair', hair, hairOffered, deps.hair);
      hair.hidden = !hairOffered;
      const soft = sections.get('soft')!;
      const softOffered = deps.softTissue !== undefined && (deps.showSoftTissue?.() ?? false);
      if (deps.softTissue !== undefined) sync('softTissue', soft, softOffered, deps.softTissue);
      joints.hidden = !handsOffered && !jointsOffered;
      parts.hidden = !partsOffered && !springOffered;
      soft.hidden = !softOffered;
      renderHeaders();
      reveal();
      if (opened) {
        if (creator()) openOnly('intake');
        else intake.open = true;
      }
      renderNext();
      const focus = deps.focus?.() ?? null;
      if (focus !== null) {
        for (const [sid, node] of sections) {
          if (sid !== focus) node.hidden = true;
          else node.open = true;
        }
        for (const row of nextRows.values()) row.hidden = true;
      }
    };
    const unsubscribe = store.subscribe(render);
    const unsubscribeIntake = deps.intakeChanged?.(render) ?? (() => undefined);
    const unsubscribeFocus = deps.focusChanged?.(render) ?? (() => undefined);
    const unsubscribeSubject = deps.subjectChanged?.(render) ?? (() => undefined);
    const unsubscribeProgress = deps.progressChanged?.(() => {
      renderHeaders();
      renderNext();
    }) ?? (() => undefined);
    render();
    return { dispose() { unsubscribe(); unsubscribeIntake(); unsubscribeFocus(); unsubscribeSubject(); unsubscribeProgress(); for (const m of mounted.values()) m.handle.dispose(); root.remove(); } };
  };
}
